import { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Calendar, Clock, Package, CheckCircle, Truck, ChevronRight, Archive, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const History = () => {
  const [tab, setTab] = useState("today");
  const [history, setHistory] = useState([]);
  const [archives, setArchives] = useState([]);
  const [selectedArchive, setSelectedArchive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tab === "today") fetchHistory();
    else fetchArchives();
  }, [tab]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/history`);
      setHistory(res.data);
    } catch (e) {
      toast.error("Failed to fetch history");
    } finally {
      setLoading(false);
    }
  };

  const fetchArchives = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/history/archives`);
      setArchives(res.data);
    } catch (e) {
      toast.error("Failed to fetch archives");
    } finally {
      setLoading(false);
    }
  };

  const openArchive = async (date) => {
    try {
      const res = await axios.get(`${API}/history/archives/${date}`);
      setSelectedArchive(res.data);
    } catch (e) {
      toast.error("Archive not found");
    }
  };

  const activityIcon = (type) => ({
    order_created: "📦", order_assigned: "🚚", order_status_changed: "✅",
    rider_added: "👤", rider_deleted: "❌", delivery_charge_updated: "💵",
    cash_collected: "💰", rider_expense: "💸", system_clear: "🧹",
    daily_reset: "🔄", order_deleted: "🗑️",
  }[type] || "📝");

  const statusColor = {
    delivered: "bg-emerald-100 text-emerald-700",
    assigned: "bg-blue-100 text-blue-700",
    pending: "bg-slate-100 text-slate-600",
    cancelled: "bg-red-100 text-red-700",
  };

  const chargeColor = {
    collected: "bg-emerald-100 text-emerald-700",
    pending: "bg-amber-100 text-amber-700",
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="history-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="history-title">History</h1>
        <p className="text-slate-500 mt-2">Activity log and past day archives</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit" data-testid="history-tabs">
        <button
          onClick={() => { setTab("today"); setSelectedArchive(null); }}
          className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${tab === "today" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
          data-testid="tab-today"
        >
          <Activity size={14} className="inline mr-1.5 -mt-0.5" />Today
        </button>
        <button
          onClick={() => { setTab("archives"); setSelectedArchive(null); }}
          className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${tab === "archives" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
          data-testid="tab-archives"
        >
          <Archive size={14} className="inline mr-1.5 -mt-0.5" />Past Days
        </button>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-3">
          {[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-slate-100 rounded-md" />)}
        </div>
      ) : tab === "today" ? (
        /* Today's Activity */
        <div className="bg-white border border-slate-200 shadow-sm rounded-md" data-testid="today-activity">
          <div className="p-5 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">Today's Activity ({history.length})</h2>
          </div>
          <div className="divide-y divide-slate-50">
            {history.length === 0 ? (
              <div className="p-12 text-center text-slate-400">
                <Clock size={32} className="mx-auto mb-3 opacity-40" />
                <p>No activity yet today</p>
              </div>
            ) : history.map((item) => (
              <div key={item.id} className="px-5 py-3.5 hover:bg-slate-50/50 transition-colors" data-testid="history-item">
                <div className="flex items-center gap-3">
                  <span className="text-lg">{activityIcon(item.activity_type)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{item.description}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-slate-400">
                        {format(new Date(item.timestamp), "hh:mm a")}
                      </span>
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-slate-100 text-slate-500 uppercase tracking-wider">
                        {item.activity_type.replace(/_/g, " ")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : selectedArchive ? (
        /* Archive Detail View */
        <div className="space-y-5" data-testid="archive-detail">
          <Button variant="ghost" onClick={() => setSelectedArchive(null)} className="hover:bg-slate-100" data-testid="back-to-archives">
            <ChevronRight size={16} className="mr-1 rotate-180" /> Back to archives
          </Button>

          <div className="flex items-center gap-3 mb-2">
            <Calendar size={20} className="text-slate-500" />
            <h2 className="text-2xl font-bold text-slate-900">
              {format(new Date(selectedArchive.date + "T00:00:00"), "EEEE, dd MMM yyyy")}
            </h2>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="archive-summary">
            {[
              { label: "Orders", value: selectedArchive.summary?.total_orders || 0 },
              { label: "Delivered", value: selectedArchive.summary?.delivered || 0 },
              { label: "Pending", value: selectedArchive.summary?.pending || 0 },
              { label: "Charges", value: `₹${(selectedArchive.summary?.total_charges || 0).toFixed(0)}` },
              { label: "Cash", value: `₹${(selectedArchive.summary?.total_cash || 0).toFixed(0)}` },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-md p-4 text-center">
                <p className="text-2xl font-bold text-slate-900">{s.value}</p>
                <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mt-1">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Archived Orders */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-md">
            <div className="p-5 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-900">Orders ({selectedArchive.orders?.length || 0})</h3>
            </div>
            {(!selectedArchive.orders || selectedArchive.orders.length === 0) ? (
              <div className="p-8 text-center text-slate-400">No orders</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="archive-orders-table">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/50">
                      <th className="text-left p-3 font-semibold text-slate-600">Customer</th>
                      <th className="text-left p-3 font-semibold text-slate-600">Address</th>
                      <th className="text-left p-3 font-semibold text-slate-600">Rider</th>
                      <th className="text-left p-3 font-semibold text-slate-600">Charge</th>
                      <th className="text-left p-3 font-semibold text-slate-600">Status</th>
                      <th className="text-left p-3 font-semibold text-slate-600">Charge Status</th>
                      <th className="text-left p-3 font-semibold text-slate-600">Cash</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedArchive.orders.map((o, i) => (
                      <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                        <td className="p-3">
                          <p className="font-medium text-slate-900">{o.customer_name}</p>
                          <p className="text-xs text-slate-400">{o.customer_phone}</p>
                        </td>
                        <td className="p-3 text-slate-600 text-xs">{o.delivery_address}</td>
                        <td className="p-3 text-slate-700">{o.rider_name || "-"}</td>
                        <td className="p-3 font-semibold text-slate-900">₹{(o.delivery_charge || 0).toFixed(2)}</td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusColor[o.status] || "bg-slate-100"}`}>
                            {o.status}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${chargeColor[o.delivery_charge_status] || "bg-slate-100"}`}>
                            {o.delivery_charge_status}
                          </span>
                        </td>
                        <td className="p-3 text-slate-700">
                          {(o.cash_collected || 0) > 0 ? `₹${o.cash_collected.toFixed(2)}` : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Archived Activity */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-md">
            <div className="p-5 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-900">Activity Log ({selectedArchive.history?.length || 0})</h3>
            </div>
            <div className="divide-y divide-slate-50 max-h-[400px] overflow-y-auto">
              {(!selectedArchive.history || selectedArchive.history.length === 0) ? (
                <div className="p-8 text-center text-slate-400">No activity</div>
              ) : selectedArchive.history.map((h, i) => (
                <div key={i} className="px-5 py-3 hover:bg-slate-50/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="text-lg">{activityIcon(h.activity_type)}</span>
                    <div>
                      <p className="text-sm text-slate-800">{h.description}</p>
                      <span className="text-xs text-slate-400">{format(new Date(h.timestamp), "hh:mm a")}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        /* Archives List */
        <div className="bg-white border border-slate-200 shadow-sm rounded-md" data-testid="archives-list">
          <div className="p-5 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">Past Days ({archives.length})</h2>
          </div>
          <div className="divide-y divide-slate-50">
            {archives.length === 0 ? (
              <div className="p-12 text-center text-slate-400">
                <Archive size={32} className="mx-auto mb-3 opacity-40" />
                <p>No archives yet</p>
                <p className="text-xs mt-1">Data is archived automatically at 6:00 AM IST daily</p>
              </div>
            ) : archives.map((a) => (
              <div
                key={a.date}
                onClick={() => openArchive(a.date)}
                className="px-5 py-4 hover:bg-slate-50 cursor-pointer transition-colors flex items-center justify-between group"
                data-testid={`archive-${a.date}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-md flex items-center justify-center">
                    <Calendar size={18} className="text-slate-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{format(new Date(a.date + "T00:00:00"), "EEEE, dd MMM yyyy")}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {a.summary?.total_orders || 0} orders · {a.summary?.delivered || 0} delivered · ₹{(a.summary?.total_charges || 0).toFixed(0)} charges
                    </p>
                  </div>
                </div>
                <ChevronRight size={18} className="text-slate-300 group-hover:text-slate-500 transition-colors" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default History;
